import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MagnifyingGlass, LockSimple, LockSimpleOpen, SealCheck, Crown, Clock, GameController, TrendUp, ArrowRight } from "@phosphor-icons/react";
import { api } from "@/lib/api";

function Row({ icon, label, value, sub }) {
  return (
    <div className="flex items-start gap-3 border-b border-black/30 py-2.5 last:border-0" data-testid={`rsn-row-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <span className="mt-0.5 text-[#ff2a55]">{icon}</span>
      <div className="min-w-0 flex-1">
        <div className="flex items-baseline justify-between gap-3">
          <span className="text-[10px] uppercase tracking-[0.2em] text-[#8c7e62]">{label}</span>
          <span className="truncate font-display text-xl leading-none text-[#f4ead3]">{value}</span>
        </div>
        {sub && <div className="mt-1 text-xs leading-relaxed text-[#aaa]">{sub}</div>}
      </div>
    </div>
  );
}

export default function RsnChecker() {
  const [name, setName] = useState("");
  const [res, setRes] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const go = async (e) => {
    e?.preventDefault();
    const n = name.trim();
    if (!n || busy) return;
    setBusy(true); setErr(""); setRes(null);
    try {
      const { data } = await api.post("/rsn/check", { name: n });
      setRes(data);
    } catch (e2) {
      setErr(e2?.response?.data?.detail || "Jagex did not answer. Try again in a minute.");
    } finally { setBusy(false); }
  };

  const c = res?.card;
  return (
    <section className="mx-auto max-w-[1500px] px-5 py-20 md:px-10 md:py-28" data-testid="rsn-checker-section">
      <div className="mb-10 text-center">
        <div className="overline-label mb-3">Free tool — same engine as our Discord /rsn</div>
        <h2 className="font-display text-5xl leading-[0.9] text-[#f4ead3] md:text-7xl">Check any name</h2>
        <p className="mx-auto mt-3 max-w-xl text-sm text-[#aaa]">Bound or free? Banned? Last played? OSRS or RS3 totals — plus our worth read with real shop comps.</p>
      </div>
      <form onSubmit={go} className="mx-auto flex max-w-xl gap-2">
        <input value={name} onChange={(e) => setName(e.target.value)} maxLength={12} placeholder="e.g. rain"
          className="field !py-3 text-center font-display text-2xl tracking-wide" data-testid="rsn-checker-input" />
        <button className="btn-bomb shrink-0 !px-5" disabled={busy || !name.trim()} data-testid="rsn-checker-button">
          {busy ? "..." : <MagnifyingGlass size={20} weight="bold" />}
        </button>
      </form>
      <AnimatePresence>
        {err && <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="mx-auto mt-6 max-w-xl border border-[#ff2a55]/50 bg-[#14090d] p-4 text-center text-sm text-[#ff8aa0]" data-testid="rsn-checker-error">{err}</motion.div>}
        {res && c && (
          <motion.div initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} className="osrs-panel mx-auto mt-6 max-w-xl p-6" data-testid="rsn-checker-result">
            <div className="mb-1 flex items-baseline justify-between gap-3">
              <span className="font-display text-4xl text-[#f4ead3]">🔍 {res.name}</span>
              <span className={`inline-flex items-center gap-1 text-xs font-bold uppercase tracking-widest ${c.bound.status === "Unbound" ? "text-[#35d07f]" : "text-[#ff2a55]"}`}>{c.bound.status === "Unbound" ? <LockSimpleOpen size={14} /> : <LockSimple size={14} />} {c.bound.status}</span>
            </div>
            <p className="mb-3 text-xs text-[#aaa]">{c.bound.reason}</p>
            <Row icon={<SealCheck size={18} />} label="Status" value={`${c.status.emoji} ${c.status.status}`} sub={c.status.reason} />
            <Row icon={<SealCheck size={18} />} label="Banned?" value={`${c.banned.emoji} ${c.banned.status}`} sub="No public ban API exists — this means no ban signal found." />
            <Row icon={<Crown size={18} />} label="Members" value={`${c.members.emoji} ${c.members.status}`} sub={c.members.reason} />
            <Row icon={<Clock size={18} />} label="Last activity" value={`${c.activity.emoji} ${c.activity.status}`} sub={c.activity.reason} />
            <Row icon={<GameController size={18} />} label="RS3 Total" value={`Lvl ${c.rs3_total.level.toLocaleString()}`} sub={`XP: ${c.rs3_total.xp.toLocaleString()}`} />
            <Row icon={<GameController size={18} />} label="OSRS Total" value={`Lvl ${c.osrs_total.level.toLocaleString()}`} sub={`XP: ${c.osrs_total.xp.toLocaleString()}`} />
            <Row icon={<TrendUp size={18} />} label={`Worth — tier ${res.worth.tier || res.style.tier}`} value={`${res.style.score}/100`} sub={`${res.worth.base}${res.worth.cite ? `. ${res.worth.cite}` : ""}`} />
            <div className="mt-3 border border-[#ff981f]/50 bg-black/40 p-3 text-center" data-testid="rsn-checker-band">
              <div className="text-[10px] uppercase tracking-[0.2em] text-[#8c7e62]">Est. value</div>
              <div className="font-display text-3xl text-[#ffe83a]">{res.worth.band || "Ask staff"}</div>
              {(res.worth.anchors || []).length > 0 && (
                <div className="mt-2 flex flex-wrap justify-center gap-2">
                  {res.worth.anchors.map((a) => <a key={a.title} href={`/shop?q=${encodeURIComponent(a.title)}`} className="border border-[#796542] px-2 py-1 text-xs text-[#ff981f] hover:text-[#ffe83a]" data-testid={`rsn-anchor-${a.title}`}>{a.title} @ {a.price}</a>)}
                </div>
              )}
            </div>
            <div className="mt-4 flex flex-wrap gap-2">
              <a href="/shop" className="btn-bomb !py-2 flex items-center gap-2" data-testid="rsn-checker-shop-link">Shop one-of-one names <ArrowRight size={14} /></a>
              <a href="https://discord.gg/S5WjhmPUN" target="_blank" rel="noreferrer" className="btn-ghost !py-2" data-testid="rsn-checker-discord-link">Claim it in Discord</a>
            </div>
            <div className="mt-3 text-[11px] text-[#666]">Live Jagex data{res.cached ? " (cached, fresh within 24h)" : ""}. Some data may be outdated — confirm free names in-game.</div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
